import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, switchMap, take, throwError } from 'rxjs';
import { getConfig } from '../config.service';

// Functional HTTP interceptor that adds Bearer token to API requests
export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const oidcSecurityService = inject(OidcSecurityService);

  // Only add auth header for requests to our middleware API
  if (!req.url.startsWith(getConfig().api.middlewareUrl)) {
    return next(req);
  }

  const withToken = (token: string) =>
    req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });

  return oidcSecurityService.getAccessToken().pipe(
    take(1),
    switchMap((token) => next(token ? withToken(token) : req)),
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // Token expired or invalid: refresh the session once and retry the request.
      // Only a failed refresh sends the user to the login. A 401 on the retried
      // request is reported to the caller instead: the API also answers 401 for a
      // server-side UnauthorizedAccessException (see ErrorHandlingMiddleware), which
      // a new login would not fix.
      return oidcSecurityService.forceRefreshSession().pipe(
        switchMap(() => oidcSecurityService.getAccessToken().pipe(take(1))),
        catchError((refreshError) => {
          // Refresh failed, redirect to login
          oidcSecurityService.authorize();
          return throwError(() => refreshError);
        }),
        switchMap((newToken) => {
          if (!newToken) {
            // No token after refresh, redirect to login
            oidcSecurityService.authorize();
            return throwError(() => error);
          }
          return next(withToken(newToken));
        })
      );
    })
  );
};
